import React, { useState } from "react";
import { Box, Typography, Button, CircularProgress, useMediaQuery } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import TransactionTypeBadge from "src/views/pages/Transaction/components/TransactionList/TransactionTypeBadge";

const truncateAddress = (address) => {
  if (!address || address.length <= 14) return address;
  return `${address.slice(0, 8)}…${address.slice(-6)}`;
};

/**
 * One row in the per-address "review individually" drill-down (see index.js - this only ever
 * renders once a user has picked a specific address from AddressGroupsPanel, never in a flat
 * all-3944-transactions list). Deliberately just two actions, no separate confirm step: "Mine"
 * marks it a transfer immediately, pre-paired with the suggestion if there is one; "Not mine"
 * dismisses it. An earlier version routed "review this one" into a separate drawer with its own
 * three-button "confirm match / mark as transfer / not a transfer" flow - removed as an
 * unnecessary extra step once the suggestion is already shown inline here.
 */
const CandidateListItem = ({ candidate, resolveExchangeImage, formatDateTime, onConfirm, onDismiss }) => {
  const theme = useTheme();
  // The page is wide enough on anything but a phone (see index.js's maxWidth: 1100) to show the
  // full address instead of always truncating it.
  const hasRoom = useMediaQuery(theme.breakpoints.up("sm"));
  const [submitting, setSubmitting] = useState(null); // "confirm" | "dismiss" | null
  const exchangeImg = resolveExchangeImage(candidate.source) || `${process.env.REACT_APP_IMAGE_BASE_URL}/Transaction/exchange.svg`;
  const [datePart] = (formatDateTime(candidate.txnDate) || "").split("\n");
  const directionColor = candidate.direction === "IN" ? theme.palette.semantic.transferIn : theme.palette.semantic.transferOut;
  const suggestion = candidate.suggestedMatch;

  const runAction = async (type, fn) => {
    setSubmitting(type);
    try {
      await fn();
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.bg4box,
        borderRadius: "14px",
        padding: "16px",
        marginBottom: "10px",
        border: `1px solid ${theme.palette.background.border}`,
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" sx={{ gap: "12px" }}>
          <Box sx={{ width: 5, height: 34, borderRadius: "3px", backgroundColor: directionColor, flexShrink: 0 }} />
          <img
            src={exchangeImg}
            alt={candidate.source}
            style={{ width: 22, height: 22 }}
            onError={(e) => {
              e.target.src = `${process.env.REACT_APP_IMAGE_BASE_URL}/Transaction/exchange.svg`;
            }}
          />
          <Box>
            <Typography variant="body2" color="primary" style={{ fontWeight: 600 }}>
              {candidate.direction === "IN" ? "Received" : "Sent"} {candidate.quantity} {candidate.coin}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {candidate.walletName ? `${candidate.walletName} (${candidate.source})` : candidate.source} &middot; {datePart}
              {candidate.counterpartyAddress && (
                <> &middot; <span style={{ fontFamily: "monospace" }}>{hasRoom ? candidate.counterpartyAddress : truncateAddress(candidate.counterpartyAddress)}</span></>
              )}
            </Typography>
          </Box>
        </Box>
        <TransactionTypeBadge type={candidate.localTxnType} label={null} size={18} />
      </Box>

      {suggestion && (
        <Box
          sx={{
            marginTop: "12px",
            marginLeft: "17px",
            padding: "10px 12px",
            borderRadius: "8px",
            backgroundColor: theme.palette.background.main,
          }}
        >
          <Box display="flex" alignItems="flex-start" justifyContent="space-between">
            <Box>
              <Typography variant="body2" color="primary" style={{ fontWeight: 700 }}>
                Looks like a transfer between your own wallets
              </Typography>
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 2 }}>
                {candidate.direction === "IN" ? "Sent" : "Received"} {suggestion.quantity} {candidate.coin} on {suggestion.source}
              </Typography>
            </Box>
            <Box
              sx={{
                flexShrink: 0,
                marginLeft: "10px",
                padding: "2px 8px",
                borderRadius: "999px",
                backgroundColor: theme.palette.semantic.reviewAccent,
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              {suggestion.confidencePercent}% match
            </Box>
          </Box>
          {suggestion.matchReason && (
            <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 6, opacity: 0.85 }}>
              <span style={{ fontWeight: 600 }}>How we found it:</span> {suggestion.matchReason}
            </Typography>
          )}
        </Box>
      )}

      <Box display="flex" alignItems="center" justifyContent="flex-end" style={{ marginTop: "14px" }}>
        <Button
          size="small"
          variant="outlined"
          disabled={!!submitting}
          onClick={() => runAction("dismiss", onDismiss)}
          style={{ color: theme.palette.semantic.negative, borderColor: theme.palette.semantic.negative, textTransform: "none", marginRight: 12, minWidth: 92 }}
        >
          {submitting === "dismiss" ? <CircularProgress size={16} color="inherit" /> : "Not mine"}
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={!!submitting}
          onClick={() => runAction("confirm", onConfirm)}
          style={{ textTransform: "none", minWidth: 92 }}
        >
          {submitting === "confirm" ? <CircularProgress size={16} color="inherit" /> : "Mine"}
        </Button>
      </Box>
    </Box>
  );
};

export default CandidateListItem;
